// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@ces/App/Components/Notify";
import { checkoutPages } from "@ces/XDL/constants";
import { FulfillmentMethod } from "@ces/XDL/types";
import { parseBoolean } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FunctionComponent } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        // XNG-Change
        cartNotPriced: cart?.cartNotPriced,
        hasInsufficientInventory: cart?.hasInsufficientInventory && cart?.fulfillmentMethod === FulfillmentMethod.Ship,
        hasInvalidPrice: hasProductsWithInvalidPrice(cart),
        failedToGetRealTimeInventory: cart?.failedToGetRealTimeInventory,
        pickUpAndItemOutOfStock: cart?.properties["pickUpAndItemOutOfStock"],
        cartHasItemsWithoutPriceOrRequireQuote: cart?.properties["cartHasItemsWithoutPriceOrRequireQuote"],
        isSpecialOrderRequested: cart?.properties["isSpecialOrderRequestedForOutOfStockItems"],
        creditHold: getSession(state)?.properties.creditHold,
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        showOutOfStockMessage: parseBoolean(cart?.properties?.pickupHasOutOfStock),
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
    // XNG-Change
    notify?: TypographyProps;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({
    cart,
    // XNG-Change
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    creditHold,
    isCheckoutPage,
    showOutOfStockMessage,
}: Props) => {
    // XNG-Changes
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    const cartLines = cart.cartLines || [];

    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);

    // XNG-Changes
    const countQuotes = cart.quoteRequiredCount;

    // XNG-Changes
    return (
        <>
            {cart.isAwaitingApproval && (
                <Notify
                    showClose={false}
                    type="info"
                    fullWidth={true}
                    data-test-selector="cart_approvingOrderMessage"
                    // XNG-Changes
                    {...styles.notify}
                >
                    {translate("Approving Order for {0}", cart.initiatedByUserName)}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify
                    type="warning"
                    showClose={false}
                    fullWidth={true}
                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                    // XNG-Changes
                    {...styles.notify}
                >
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                    // XNG-Changes
                    {...styles.notify}
                >
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventory && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                    // XNG-Changes
                    {...styles.notify}
                >
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    // XNG-Changes
                    {...styles.notify}
                >
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    // XNG-Changes
                    {...styles.notify}
                >
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
            {!isCheckoutPage && parseBoolean(creditHold) && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    // XNG-Changes
                    {...styles.notify}
                >
                    {siteMessage("Custom_Cart_CreditHold")}
                </Notify>
            )}
            {!isCheckoutPage && !!countQuotes && !!siteMessage("Custom_Cart_Quotes_Count").toString().length && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    // XNG-Changes
                    {...styles.notify}
                >
                    {siteMessage("Custom_Cart_Quotes_Count", countQuotes.toString(), cartLines.length.toString())}
                </Notify>
            )}
            {/* XNG-Changes */}
            {showOutOfStockMessage && (
                <Notify showClose={false} type="warning" fullWidth={true} {...styles.notify}>
                    {siteMessage("Custom_Cart_Pickup_OOS_Message")}
                </Notify>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
